import type { Object3D } from 'three';
import {
  normalizeComponentInstanceStringValue,
  readComponentInstanceGlbHierarchyMeta,
  readComponentInstanceImportedModelHierarchyMeta,
  readComponentInstanceImportedModelMetadata,
  readComponentInstanceImportedModelSelectionTarget,
  readComponentInstancePuppyMetadata,
  readComponentInstanceStringValue,
  isInsideSkippedComponentInstanceTraversalSubtree,
} from './utils/ComponentInstanceMetadata';

type ObjectMetadataStringReader = (object: Object3D) => string | null | undefined;

/**
 * 组件实例变换根解析选项。
 */
export interface ResolveComponentInstanceTransformRootOptions {
  /** 根据场景对象 ID 查找 Object3D，优先由 Viewer 提供。 */
  readonly getSceneObjectById?: (objectId: string) => Object3D | null;
  /** 读取选择代理对象 ID；用于让 imported-model metadata 服务以依赖注入方式接入。 */
  readonly resolveSelectionProxyObjectId?: ObjectMetadataStringReader;
  /** 读取 GLB 层级声明的选择对象 ID。 */
  readonly resolveHierarchySelectionObjectId?: ObjectMetadataStringReader;
  /** 读取装配节点 ID，用于同一实例内的代理根校验。 */
  readonly resolveAssemblyNodeId?: ObjectMetadataStringReader;
  /**
   * 判断候选根节点是否不能承载组件 transform。
   *
   * 设计说明：
   * 拓扑面、边、点代理可能和真实组件根共享 assemblyNodeId，但它们只承载选择/高亮语义。
   * 通用解析器不直接依赖这些 CAD 具体类型，而是通过注入谓词保持依赖倒置。
   */
  readonly isRejectedTransformRoot?: (object: Object3D) => boolean;
  /**
   * 当解析命中不可写代理时，按 assemblyNodeId 从调用方场景中查找真实组件根。
   *
   * @param assemblyNodeId 装配实例节点 ID。
   * @param sourceObject 触发本次解析的源对象。
   * @returns 可写 transform 根；找不到时返回 null。
   */
  readonly findTransformRootByAssemblyNodeId?: (
    assemblyNodeId: string,
    sourceObject: Object3D,
  ) => Object3D | null;
}

/**
 * 调用可选注入读取器并规范化返回值。
 *
 * @param reader 外部读取器。
 * @param object 场景对象。
 * @returns 有效字符串；不存在时返回空字符串。
 */
function readInjectedStringValue(
  reader: ObjectMetadataStringReader | undefined,
  object: Object3D,
): string {
  return normalizeComponentInstanceStringValue(reader?.(object));
}

/**
 * 读取选择代理对象 ID。
 *
 * @param object 场景对象。
 * @param options 解析选项。
 * @returns 选择代理对象 uuid；缺失时返回空字符串。
 */
function readSelectionProxyObjectId(
  object: Object3D,
  options: ResolveComponentInstanceTransformRootOptions,
): string {
  return readInjectedStringValue(options.resolveSelectionProxyObjectId, object)
    || readComponentInstanceStringValue(readComponentInstanceImportedModelMetadata(object), 'selectionProxyObjectId')
    || readComponentInstanceStringValue(object.userData, 'importedSelectionProxyObjectId');
}

/**
 * 读取 hierarchy 声明的选择对象 ID。
 *
 * @param object 场景对象。
 * @param options 解析选项。
 * @returns 选择对象 uuid；缺失时返回空字符串。
 */
function readHierarchySelectionObjectId(
  object: Object3D,
  options: ResolveComponentInstanceTransformRootOptions,
): string {
  return readInjectedStringValue(options.resolveHierarchySelectionObjectId, object)
    || readComponentInstanceStringValue(readComponentInstanceImportedModelHierarchyMeta(object), 'selectionObjectId')
    || readComponentInstanceStringValue(readComponentInstanceGlbHierarchyMeta(object), 'selectionObjectId');
}

/**
 * 判断对象是否携带足以代表装配实例的运行时身份。
 *
 * @param object 场景对象。
 * @param options 解析选项。
 * @returns 存在稳定装配节点 ID 时返回 `true`。
 */
function hasAssemblyInstanceIdentity(
  object: Object3D,
  options: ResolveComponentInstanceTransformRootOptions,
): boolean {
  return readAssemblyNodeId(object, options).length > 0;
}

/**
 * 判断对象是否可作为装配实例 transform 身份根。
 *
 * 设计说明：
 * `semanticKind=part/assembly` 只描述导入层级语义，不能单独代表当前项目中的组件实例。
 * transform 写入权限必须来自稳定 `assemblyNodeId`，否则刷新重载后的可见 mesh 会抢占父级实例根。
 *
 * @param object 场景对象。
 * @param options 解析选项。
 * @returns 是否可作为装配实例 transform 身份根。
 */
function isAssemblyInstanceTransformRoot(
  object: Object3D,
  options: ResolveComponentInstanceTransformRootOptions,
): boolean {
  return hasAssemblyInstanceIdentity(object, options);
}

/**
 * 从候选对象向父级查找最近的组件实例身份根。
 *
 * @param object 候选对象。
 * @returns 最近的携带 assemblyNodeId 的实例身份根；不存在时返回 null。
 */
function findNearestAssemblyInstanceTransformRoot(
  object: Object3D,
  options: ResolveComponentInstanceTransformRootOptions,
): Object3D | null {
  let current: Object3D | null = object;
  while (current) {
    if (isAssemblyInstanceTransformRoot(current, options)) {
      return current;
    }
    current = current.parent;
  }
  return null;
}

/**
 * 查找同一装配节点身份下最外层的可变换语义根。
 *
 * 设计原因：OpenProject 项目模型会在项目节点容器下再挂载包根对象，两者可能拥有相同的
 * assemblyNodeId。拖拽和提交必须落到项目节点容器，否则提交读取项目节点绑定时会读到旧矩阵。
 *
 * @param object 当前已经解析到的候选变换根。
 * @param options 变换根解析选项。
 * @returns 同一 assemblyNodeId 父链上的最外层语义根；找不到时返回当前候选。
 */
function findOutermostAssemblyNodeTransformRoot(
  object: Object3D,
  options: ResolveComponentInstanceTransformRootOptions,
): Object3D {
  const sourceAssemblyNodeId = readAssemblyNodeId(object, options);
  if (!sourceAssemblyNodeId) {
    return object;
  }

  let current: Object3D | null = object;
  let outermostRoot: Object3D = object;
  while (current) {
    const currentAssemblyNodeId = readAssemblyNodeId(current, options);
    if (
      currentAssemblyNodeId === sourceAssemblyNodeId
      && isAssemblyInstanceTransformRoot(current, options)
    ) {
      outermostRoot = current;
    }
    current = current.parent;
  }

  return outermostRoot;
}

/**
 * 读取对象的装配节点 ID。
 *
 * @param object 场景对象。
 * @returns assemblyNodeId；缺失时返回空字符串。
 */
function readAssemblyNodeId(
  object: Object3D,
  options: ResolveComponentInstanceTransformRootOptions,
): string {
  const injectedNodeId = readInjectedStringValue(options.resolveAssemblyNodeId, object);
  if (injectedNodeId) {
    return injectedNodeId;
  }

  const metadataNodeId = readComponentInstanceStringValue(
    readComponentInstanceImportedModelMetadata(object),
    'assemblyNodeId',
  ) || readComponentInstanceStringValue(
    readComponentInstanceImportedModelSelectionTarget(object),
    'assemblyNodeId',
  );
  if (metadataNodeId) {
    return metadataNodeId;
  }

  const directNodeId = readComponentInstanceStringValue(object.userData, 'assemblyNodeId');
  if (directNodeId) {
    return directNodeId;
  }

  const puppyNodeId = readComponentInstanceStringValue(
    readComponentInstancePuppyMetadata(object),
    'assemblyNodeId',
  );
  if (puppyNodeId) {
    return puppyNodeId;
  }

  const legacySelectionTarget = (
    object.userData as { importedSelectionTarget?: unknown } | null | undefined
  )?.importedSelectionTarget;
  return readComponentInstanceStringValue(
    legacySelectionTarget,
    'assemblyNodeId',
  );
}

/**
 * 读取组件实例装配节点 ID。
 *
 * 设计说明：
 * Move/Rotate、Mate 与历史回放都需要同一套 assemblyNodeId 解析规则。
 * 将该读取能力从 transform root 解析器中导出，避免各业务模块重复理解 imported-model 元数据结构。
 *
 * @param object 场景对象。
 * @param options 解析选项。
 * @returns 装配节点 ID；缺失时返回空字符串。
 */
export function readComponentInstanceAssemblyNodeId(
  object: Object3D,
  options: ResolveComponentInstanceTransformRootOptions = {},
): string {
  return readAssemblyNodeId(object, options);
}

/**
 * 判断候选根节点是否被调用方拒绝。
 *
 * @param object 候选根节点。
 * @param options 解析选项。
 * @returns 是否不可作为 transform 根。
 */
function isRejectedTransformRoot(
  object: Object3D,
  options: ResolveComponentInstanceTransformRootOptions,
): boolean {
  return options.isRejectedTransformRoot?.(object) === true;
}

/**
 * 当候选对象不可写时按 assemblyNodeId 尝试回退到真实组件根。
 *
 * @param sourceObject 源对象。
 * @param rejectedObject 被拒绝的候选对象。
 * @param options 解析选项。
 * @returns 可写根节点；找不到时返回 null。
 */
function resolveFallbackTransformRoot(
  sourceObject: Object3D,
  rejectedObject: Object3D,
  options: ResolveComponentInstanceTransformRootOptions,
): Object3D | null {
  const assemblyNodeId = readAssemblyNodeId(sourceObject, options)
    || readAssemblyNodeId(rejectedObject, options);
  if (!assemblyNodeId) {
    return null;
  }

  const fallback = options.findTransformRootByAssemblyNodeId?.(assemblyNodeId, sourceObject) ?? null;
  if (!fallback || fallback === rejectedObject || isRejectedTransformRoot(fallback, options)) {
    return null;
  }

  return fallback;
}

/**
 * 对解析出的候选根节点执行可写性校验。
 *
 * @param candidate 候选根节点。
 * @param sourceObject 源对象。
 * @param options 解析选项。
 * @returns 可写根节点；候选不可写且无回退时返回 null。
 */
function resolveAcceptedTransformRoot(
  candidate: Object3D | null,
  sourceObject: Object3D,
  options: ResolveComponentInstanceTransformRootOptions,
): Object3D | null {
  if (!candidate) {
    return null;
  }
  if (!isRejectedTransformRoot(candidate, options)) {
    return candidate;
  }
  return resolveFallbackTransformRoot(sourceObject, candidate, options);
}

/**
 * 解析可提交组件位姿的最终根节点。
 *
 * 设计说明：版本切换后的项目模型需要把 packageRoot 提升到项目节点容器；
 * 旧导入链路又可能把可见 Mesh 标记为 part 语义但禁止直接写 transform。
 * 因此这里先按同 assemblyNodeId 提升候选，再做可写性校验和真实根回退，
 * 回退成功后再提升一次，确保最终对象既是可写根，也是提交快照读取的外层项目节点。
 *
 * @param candidate 候选根节点。
 * @param sourceObject 触发解析的源对象。
 * @param options 解析选项。
 * @returns 可写且已提升到正确装配层级的根节点。
 */
function resolveFinalAssemblyTransformRoot(
  candidate: Object3D | null,
  sourceObject: Object3D,
  options: ResolveComponentInstanceTransformRootOptions,
): Object3D | null {
  const outermostCandidate = candidate
    ? findOutermostAssemblyNodeTransformRoot(candidate, options)
    : null;
  const acceptedRoot = resolveAcceptedTransformRoot(outermostCandidate, sourceObject, options);
  return acceptedRoot
    ? findOutermostAssemblyNodeTransformRoot(acceptedRoot, options)
    : null;
}

/**
 * 从对象父链中查找显式选择代理根。
 *
 * 设计说明：
 * 某些导入零件的可见 Mesh 自己也带 `semanticKind=part`，但真正应写 transform 的对象是
 * 父链上 `importedSelectionProxyObjectId === uuid` 的实例 Group。这里在语义根判断前先找代理根，
 * 避免直接拖拽把位姿写到 Mesh 上，导致导出仍读取旧的实例 Group 矩阵。
 *
 * @param object 当前对象。
 * @param expectedProxyObjectId 显式代理对象 ID；缺失时匹配任意同 assemblyNodeId 的自代理对象。
 * @returns 代理根对象。
 */
function findAncestorSelectionProxyRoot(
  object: Object3D,
  options: ResolveComponentInstanceTransformRootOptions,
  expectedProxyObjectId = '',
): Object3D | null {
  const sourceAssemblyNodeId = readAssemblyNodeId(object, options);
  let current: Object3D | null = object;

  while (current) {
    const currentProxyObjectId = readSelectionProxyObjectId(current, options);
    const currentAssemblyNodeId = readAssemblyNodeId(current, options);
    const isSelfProxy = currentProxyObjectId === current.uuid;
    const isExpectedProxy = expectedProxyObjectId.length > 0 && current.uuid === expectedProxyObjectId;
    const isSameAssemblyNode = sourceAssemblyNodeId.length > 0 && sourceAssemblyNodeId === currentAssemblyNodeId;
    const isCrossAssemblyProxy = sourceAssemblyNodeId.length > 0
      && currentAssemblyNodeId.length > 0
      && sourceAssemblyNodeId !== currentAssemblyNodeId;

    if (
      isSelfProxy
      && !isCrossAssemblyProxy
      && (isExpectedProxy || expectedProxyObjectId.length === 0 || isSameAssemblyNode)
    ) {
      return current;
    }

    current = current.parent;
  }

  return null;
}

/**
 * 尝试通过选择代理 ID 解析真正承载组件变换的对象。
 *
 * @param object 当前对象。
 * @param options 解析选项。
 * @returns 代理根对象；无法解析时返回 null。
 */
function resolveSelectionProxyRoot(
  object: Object3D,
  options: ResolveComponentInstanceTransformRootOptions,
): Object3D | null {
  const proxyObjectId = readSelectionProxyObjectId(object, options);
  if (!proxyObjectId) {
    return null;
  }

  if (proxyObjectId === object.uuid) {
    return object;
  }

  const proxyObject = options.getSceneObjectById?.(proxyObjectId) ?? null;
  if (proxyObject && isInsideSkippedComponentInstanceTraversalSubtree(proxyObject)) {
    return null;
  }
  const sourceAssemblyNodeId = readAssemblyNodeId(object, options);
  const proxyAssemblyNodeId = proxyObject ? readAssemblyNodeId(proxyObject, options) : '';
  if (
    sourceAssemblyNodeId.length > 0
    && proxyAssemblyNodeId.length > 0
    && sourceAssemblyNodeId !== proxyAssemblyNodeId
  ) {
    /**
     * 插入装配体子零件可能继承源装配根的旧 selectionProxyObjectId。
     * 该代理跨越 assemblyNodeId 边界时只能代表选择来源，不能作为组件变换根，
     * 否则拖动子零件会写到整棵插入装配体。
     */
    return null;
  }
  return proxyObject ?? findAncestorSelectionProxyRoot(object, options, proxyObjectId);
}

/**
 * 尝试通过 hierarchy selectionObjectId 解析组件根。
 *
 * @param object 当前对象。
 * @param options 解析选项。
 * @returns selectionObjectId 指向的对象；无法解析时返回 null。
 */
function resolveHierarchySelectionRoot(
  object: Object3D,
  options: ResolveComponentInstanceTransformRootOptions,
): Object3D | null {
  const selectionObjectId = readHierarchySelectionObjectId(object, options);
  if (!selectionObjectId) {
    return null;
  }

  if (selectionObjectId === object.uuid) {
    return object;
  }

  const selectionObject = options.getSceneObjectById?.(selectionObjectId) ?? null;
  if (selectionObject && isInsideSkippedComponentInstanceTraversalSubtree(selectionObject)) {
    return null;
  }
  const sourceAssemblyNodeId = readAssemblyNodeId(object, options);
  const selectionAssemblyNodeId = selectionObject ? readAssemblyNodeId(selectionObject, options) : '';
  if (
    sourceAssemblyNodeId.length > 0
    && selectionAssemblyNodeId.length > 0
    && sourceAssemblyNodeId !== selectionAssemblyNodeId
  ) {
    /**
     * 插入装配体子级可能同时继承源根的 selectionObjectId。
     * 该 ID 跨越组件实例边界时只能作为旧选择线索，不能抢占当前子零件的 transform root。
     */
    return null;
  }
  return selectionObject ?? findAncestorSelectionProxyRoot(object, options, selectionObjectId);
}

/**
 * 解析组件实例的可变换根对象。
 *
 * 设计说明：
 * imported-model 的可见 Mesh、拓扑代理、body host 与语义 part/assembly 根不是同一个对象。
 * 移动、旋转、历史、导出必须统一落到同一个组件根，否则视口里移动的是子对象，
 * STEP 导出读取的语义根仍停留在原始姿态。
 * 显式选择代理优先级高于 glb 语义根，因为导入 Mesh 可能保留 part 语义但不应该承载组件位姿。
 *
 * @param object 当前命中的或选中的场景对象。
 * @param options 解析选项。
 * @returns 可变换根；非 imported-model 对象按原对象返回。
 */
export function resolveComponentInstanceTransformRoot(
  object: Object3D | null | undefined,
  options: ResolveComponentInstanceTransformRootOptions = {},
): Object3D | null {
  if (!object) {
    return null;
  }
  if (isInsideSkippedComponentInstanceTraversalSubtree(object)) {
    return null;
  }

  let current: Object3D | null = object;
  while (current) {
    const selectionRoot = resolveSelectionProxyRoot(current, options)
      ?? resolveHierarchySelectionRoot(current, options)
      ?? findAncestorSelectionProxyRoot(current, options);
    const acceptedSelectionRoot = resolveFinalAssemblyTransformRoot(selectionRoot, current, options);
    if (acceptedSelectionRoot) {
      return acceptedSelectionRoot;
    }

    const identityRoot = findNearestAssemblyInstanceTransformRoot(current, options);
    const acceptedIdentityRoot = resolveFinalAssemblyTransformRoot(identityRoot, current, options);
    if (acceptedIdentityRoot) {
      return acceptedIdentityRoot;
    }

    current = current.parent;
  }

  return resolveFinalAssemblyTransformRoot(object, object, options);
}
